import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
/**
 * PostAccountReferralInitiation
 * -----------------------------
 * Purpose:
 *  - Full-page, post-account flow to initiate a new referral (CYP).
 *  - Skips referrer details; asks for CYP name → address (eligibility) → relationship.
 *  - On submit, creates a patient record and navigates to its overview.
 *
 * Design:
 *  - Reuses the same draft intent mechanism as pre-account to persist step patches.
 *  - Uses the post-account schema: referralInitiationCypPostAccount.
 *  - Eligibility check on address step; shows review with custom submit labels.
 *  - When postcode eligibility fails, displays an informative dialog (parity with
 *    pre-account flow which navigates to an outcome page).
 */
import { useNavigate } from 'react-router-dom';
import { HomeIcon, ExclamationCircleIcon } from '@heroicons/react/solid';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import { useAuth } from '../../hooks/useAuth';
import { useRmsState } from '../context/RmsStateContext';
import useRmsAnalytics from '../hooks/useRmsAnalytics';
import { generateIdempotencyKey } from '../utils/idempotency';
import RmsFormTemplate from '../components/RmsFormTemplate';
import { apiSaveIntentDraft, apiCheckPostcodeEligibility, apiSubmitPostAccountReferral } from '../utils/rmsApi';
import { CONSENT_NOT_GIVEN } from '../constants/outcomeCopy';
import { auth, db } from '../../firebase';
import { doc, getDoc } from 'firebase/firestore';

function normalizeUkPostcode(raw) {
  if (!raw) return '';
  let s = String(raw).toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (s.length > 3) {
    s = s.slice(0, -3) + ' ' + s.slice(-3);
  }
  return s.trim();
}

export default function PostAccountReferralInitiation() {
  const navigate = useNavigate();
  const { claims } = useAuth();
  const orgId = claims?.rmsOrgId;
  const formKey = 'referralInitiationCypPostAccount';
  // Destructure stable useCallback references to avoid re-running init effects
  // on every context version bump (see RmsStateContext versionTick).
  const { ensurePreIntentBundle, ensureReferrals } = useRmsState();
  const analytics = useRmsAnalytics(orgId);

  const [schema, setSchema] = useState(null);
  const [initialStepId, setInitialStepId] = useState(null);
  const [prospect, setProspect] = useState({ prospectId: null, clientToken: null });
  const [answers, setAnswers] = useState({});
  // Determine initiator from user claims to drive relationship options (school vs guardian)
  const [initiatedBy, setInitiatedBy] = useState(null);
  // School phase ('primary' | 'secondary') for school-rep additional referrals.
  // This flow doesn't re-ask the school, so the DOB picker can't narrow its
  // year range from the answers; inherit the phase from the rep's existing
  // patient records (the backend does the same on submit).
  const [schoolPhase, setSchoolPhase] = useState(null);
  
  // State for the "leave form" confirmation dialog
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  // Ref to hold the Promise resolver for the navigation confirmation
  const leaveResolverRef = useRef(null);
  
  // State for postcode ineligibility dialog (shown when CYP's postcode is outside service area)
  // This provides parity with the pre-account flow which navigates to an outcome page
  const [showIneligibleDialog, setShowIneligibleDialog] = useState(false);
  const [ineligibilityReason, setIneligibilityReason] = useState(null);

  const setAnswer = useCallback((qid, value) => {
    setAnswers((prev) => ({ ...prev, [qid]: value }));
  }, []);

  // Resolve initiatedBy based on current user's custom claims
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const u = auth?.currentUser;
        if (!u) return;
        const tok = await u.getIdTokenResult(true);
        const userType = String((tok?.claims || {}).userType || '').toLowerCase();
        if (cancelled) return;
        if (userType.includes('school')) {
          setInitiatedBy('school');
        } else {
          setInitiatedBy('guardian');
        }
      } catch (_) {
        setInitiatedBy('guardian');
      }
    })();
    return () => { cancelled = true };
  }, []);

  // Resolve the school rep's phase from their existing patient records so the
  // CYP DOB picker offers the same year range as the pre-account start form
  // (secondary 11-16, primary 4-11). A rep's records are all at their school,
  // so the first record with a phase is authoritative. Guardians (and reps
  // with no prior records) keep the full CYP range.
  useEffect(() => {
    if (initiatedBy !== 'school' || !orgId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const rows = await ensureReferrals({ orgId });
        for (const row of rows || []) {
          const id = row?.patientRecordId;
          if (!id) continue;
          const snap = await getDoc(doc(db, 'rms_organisations', orgId, 'patientRecords', id));
          const data = snap.exists() ? (snap.data() || {}) : {};
          const phase = data.schoolPhase || data.school?.schoolType || null;
          if (phase === 'primary' || phase === 'secondary') {
            if (!cancelled) setSchoolPhase(phase);
            return;
          }
        }
      } catch (_) {
        // no-op: picker falls back to the full CYP range
      }
    })();
    return () => { cancelled = true; };
  }, [initiatedBy, orgId, ensureReferrals]);

  // Initialize draft intent and schema
  const didInitRef = useRef(false);
  useEffect(() => {
    if (didInitRef.current) return;
    if (!initiatedBy) return; // wait until initiator is resolved
    let cancelled = false;
    (async () => {
      try {
        console.log('[PostAccountReferralInitiation] Initializing with:', { orgId, formKey, initiatedBy });
        const bundle = await ensurePreIntentBundle({
          orgId,
          formKey,
          initiatedBy,
          idempotencyKey: generateIdempotencyKey('rms-pa'),
          createIfMissing: true  // Required to create a prospect for submission
        });
        console.log('[PostAccountReferralInitiation] Bundle received:', { 
          hasSchema: !!bundle?.schema, 
          prospectId: bundle?.prospectId, 
          hasClientToken: !!bundle?.clientToken,
          answersCount: Object.keys(bundle?.answers || {}).length
        });
        if (cancelled) return;
        didInitRef.current = true;
        setSchema(bundle?.schema || null);
        setProspect({ prospectId: bundle?.prospectId || null, clientToken: bundle?.clientToken || null });
        setAnswers(bundle?.answers || {});
        if (bundle?.initialStepId) setInitialStepId(bundle.initialStepId);
        analytics.intentStarted({ formKey, prospectId: bundle?.prospectId, initiatedBy });
        // Warm draft save once
        try {
          apiSaveIntentDraft({
            orgId,
            prospectId: bundle?.prospectId,
            clientToken: bundle?.clientToken,
            formKey,
            patch: {},
            stepId: null
          }).catch(() => {});
        } catch (_) {}
      } catch (e) {
        console.error('[PostAccountReferralInitiation] Init failed:', e);
        analytics.error({ where: 'post_account_init', message: String(e?.message || e) });
      }
    })();
    return () => { cancelled = true; };
  }, [orgId, ensurePreIntentBundle, analytics, formKey, initiatedBy]);

  const handleSaveStep = useCallback(async (currentStep, stepPatch) => {
    if (!prospect.prospectId) return;
    try {
      await apiSaveIntentDraft({
        orgId,
        prospectId: prospect.prospectId,
        clientToken: prospect.clientToken,
        formKey,
        patch: stepPatch,
        stepId: currentStep?.questionId
      });
      analytics.stepSaved({ stepId: currentStep?.questionId, prospectId: prospect.prospectId, initiatedBy: 'post_account' });
    } catch (e) {
      console.error('Draft save failed', e);
      analytics.error({ where: 'saveStep_post', stepId: currentStep?.questionId, message: String(e?.message || e) });
    }
  }, [orgId, prospect.prospectId, prospect.clientToken, analytics, formKey]);

  /**
   * beforeNext - Validation hook called before advancing to the next step
   * ---------------------------------------------------------------------
   * Performs postcode eligibility check when leaving the address step.
   * This ensures the child/young person's home postcode falls within the
   * service area before allowing the referral to proceed.
   * 
   * Parity with pre-account flow:
   *  - Fires eligibilityChecked analytics event (for funnel tracking)
   *  - On failure, shows an informative dialog (pre-account navigates to outcome page)
   *  - On success, allows form progression
   */
  const beforeNext = useCallback(async (currentStep, currentAnswers) => {
    // Consent gate: a referral cannot proceed without consent. Stopped at the
    // question itself, before any of the child's details are asked for.
    // rmsSubmitPostAccountReferral repeats the check server-side.
    if (currentStep?.questionId === 'consent_confirmed') {
      const answer = currentAnswers?.consent_confirmed;
      const optionId = typeof answer === 'string' ? answer : answer?.optionId;
      if (optionId && optionId !== 'yes') {
        if (typeof analytics.eligibilityFailed === 'function') {
          analytics.eligibilityFailed({ formKey, initiatedBy, reason: CONSENT_NOT_GIVEN });
        }
        setIneligibilityReason(CONSENT_NOT_GIVEN);
        setShowIneligibleDialog(true);
        return { proceed: false };
      }
    }

    if (currentStep?.questionId === 'subject_address') {
      try {
        const rawPostcode = currentAnswers?.addr_postcode || '';
        const normalized = normalizeUkPostcode(rawPostcode);
        
        // Fire eligibilityChecked event for funnel analytics (parity with pre-account flow)
        if (typeof analytics.eligibilityChecked === 'function') {
          const outward = normalized.split(' ')[0] || null;
          analytics.eligibilityChecked({ formKey, initiatedBy, outwardCode: outward });
        }
        
        const res = await apiCheckPostcodeEligibility({
          orgId,
          prospectId: prospect.prospectId || undefined,
          postcodeRaw: rawPostcode,
          postcodeNormalized: normalized,
          usedPostcodeType: 'child_home'
        });
        
        if (res?.eligible === false) {
          analytics.eligibilityFailed({ formKey, initiatedBy, reason: res?.reasonCode || null, areaVersion: res?.areaVersion || null });
          // Show informative dialog instead of silently blocking progression
          // (Pre-account flow navigates to outcome page; post-account shows dialog
          // since user already has an account and dashboard to return to)
          setIneligibilityReason(res?.reasonCode || 'outside_area');
          setShowIneligibleDialog(true);
          return { proceed: false };
        }
        
        analytics.eligibilityPassed({ formKey, initiatedBy, areaVersion: res?.areaVersion || null });
        return { proceed: true };
      } catch (e) {
        analytics.error({ where: 'eligibility_check_post', message: String(e?.message || e), initiatedBy });
        throw e;
      }
    }
    return { proceed: true };
  }, [orgId, prospect?.prospectId, analytics, formKey, initiatedBy]);

  const handleSubmit = useCallback(async () => {
    // Validate that we have a valid prospect before submitting
    if (!prospect?.prospectId) {
      console.error('Post-account submit failed: No prospectId available. Prospect state:', prospect);
      analytics.error({ where: 'submit_post', message: 'No prospectId available for submission' });
      // Show user-friendly error - this shouldn't happen in normal flow
      alert('Unable to submit referral. Please refresh the page and try again.');
      return;
    }
    try {
      const res = await apiSubmitPostAccountReferral({
        orgId,
        prospectId: prospect.prospectId,
        clientToken: prospect.clientToken,
        formKey,
        idempotencyKey: generateIdempotencyKey('submit-pa')
      });
      try {
        await ensureReferrals({ orgId, force: true });
      } catch (_) {}
      analytics.intentSubmitted({ prospectId: prospect.prospectId, formKey, initiatedBy });
      const prId = (res?.data || res)?.patientRecordId;
      if (prId) {
        navigate(`/rms/patient/${encodeURIComponent(prId)}/overview`);
      } else {
        navigate('/rms/dashboard');
      }
    } catch (e) {
      console.error('Post-account submit failed', e);
      analytics.error({ where: 'submit_post', message: String(e?.message || e) });
      // Show user-friendly error for submit failures
      alert('Failed to initiate referral. Please try again.');
    }
  }, [prospect, orgId, formKey, ensureReferrals, navigate, analytics, initiatedBy]);

  const formContext = useMemo(() => ({
    pathway: 'cyp',
    initiatedBy,
    schoolPhase,
    currentUser: { role: 'service_user' }
  }), [initiatedBy, schoolPhase]);

  /**
   * Check if the form has any meaningful answers (is partially complete).
   * We consider the form partially complete if any answer has a non-empty value.
   */
  const hasPartialAnswers = useMemo(() => {
    return Object.values(answers).some((val) => {
      if (val == null) return false;
      if (typeof val === 'string') return val.trim() !== '';
      if (typeof val === 'object') return Object.keys(val).length > 0;
      return true;
    });
  }, [answers]);

  /**
   * onBeforeNavigateHome
   * --------------------
   * Called by RmsFormTemplate when the user clicks the home/dashboard button.
   * If the form is partially complete, we show a confirmation dialog.
   * Returns a Promise that resolves to true (proceed) or false (cancel).
   */
  const handleBeforeNavigateHome = useCallback(() => {
    // If no answers, allow immediate navigation
    if (!hasPartialAnswers) {
      return Promise.resolve(true);
    }
    
    // Show the confirmation dialog and return a Promise
    return new Promise((resolve) => {
      leaveResolverRef.current = resolve;
      setShowLeaveDialog(true);
    });
  }, [hasPartialAnswers]);

  /**
   * Handle user confirming they want to leave (go to dashboard)
   */
  const handleConfirmLeave = useCallback(() => {
    setShowLeaveDialog(false);
    if (leaveResolverRef.current) {
      leaveResolverRef.current(true);
      leaveResolverRef.current = null;
    }
  }, []);

  /**
   * Handle user cancelling (staying on the form)
   */
  const handleCancelLeave = useCallback(() => {
    setShowLeaveDialog(false);
    if (leaveResolverRef.current) {
      leaveResolverRef.current(false);
      leaveResolverRef.current = null;
    }
  }, []);

  /**
   * Handle dismissing the ineligibility dialog.
   * User can either:
   *  - Go back to edit the address (dismiss dialog, stay on form)
   *  - Return to dashboard (navigate away)
   */
  const handleDismissIneligible = useCallback(() => {
    setShowIneligibleDialog(false);
    setIneligibilityReason(null);
  }, []);

  const handleIneligibleGoToDashboard = useCallback(() => {
    setShowIneligibleDialog(false);
    setIneligibilityReason(null);
    navigate('/rms/dashboard');
  }, [navigate]);

  /**
   * Get user-friendly message for ineligibility reason codes.
   * Mirrors the messaging in the pre-account outcome page for consistency.
   */
  const getIneligibilityMessage = useCallback((reasonCode) => {
    switch (reasonCode) {
      // COPY GATE: service-user facing, not yet through Jasmine's review.
      case CONSENT_NOT_GIVEN:
        return "A referral can only go ahead once the child or young person's parent or carer has agreed to it. Where a young person is able to decide for themselves, we need their agreement instead. Please talk it through with them, then start the referral again.";
      case 'outside_area':
      case 'postcode_not_in_service_area':
        return 'The postcode you entered is outside our current service area. We are unable to accept referrals for individuals living at this address at this time.';
      case 'invalid_postcode':
        return 'The postcode you entered could not be verified. Please check the postcode and try again.';
      default:
        return 'Based on the information provided, this referral does not meet our current eligibility criteria.';
    }
  }, []);

  if (!schema) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-white to-secondary/15 flex flex-col">
        <div className="w-full px-6 md:px-10 pt-20 text-center text-gray-800">Loading… Fetching referral form</div>
      </div>
    );
  }

  return (
    <>
      <RmsFormTemplate
        schema={schema}
        answers={answers}
        setAnswer={setAnswer}
        initialStepId={initialStepId}
        onSaveStep={handleSaveStep}
        onComplete={async () => { await handleSubmit(); }}
        beforeNext={beforeNext}
        formContext={formContext}
        showBrand={false}
        brandBackTo="/rms/dashboard"
        brandBackLabel={<HomeIcon className="w-5 h-5" aria-label="Dashboard" />}
        reviewNextLabel="Initiate Referral"
        reviewLoadingLabel="Initiating Referral..."
        onBeforeNavigateHome={handleBeforeNavigateHome}
      />

      {/* Leave Form Confirmation Dialog */}
      <AlertDialog.Root open={showLeaveDialog} onOpenChange={(open) => { if (!open) handleCancelLeave(); }}>
        <AlertDialog.Portal>
          <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/40 backdrop-blur-sm z-[100]" />
          <AlertDialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-lg p-6 max-w-md w-[90vw] z-[101]">
            <AlertDialog.Title className="text-lg font-semibold text-gray-900 mb-2">
              Leave this form?
            </AlertDialog.Title>
            <AlertDialog.Description className="text-sm text-gray-600 mb-6">
              You have a partially completed referral form. Your progress is saved, so you can come back and pick up where you left off whenever you're ready.
            </AlertDialog.Description>
            <div className="flex justify-end gap-3">
              <AlertDialog.Cancel asChild>
                <button
                  type="button"
                  onClick={handleCancelLeave}
                  className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  Cancel
                </button>
              </AlertDialog.Cancel>
              <AlertDialog.Action asChild>
                <button
                  type="button"
                  onClick={handleConfirmLeave}
                  className="px-4 py-2 text-sm font-semibold text-white bg-primary border border-transparent rounded-lg hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  Go to Dashboard
                </button>
              </AlertDialog.Action>
            </div>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>

      {/* Postcode Ineligibility Dialog
          ─────────────────────────────
          Shown when the CYP's home postcode is outside the service area.
          Provides parity with the pre-account flow which navigates to an outcome page.
          User can either edit the address or return to dashboard. */}
      <AlertDialog.Root open={showIneligibleDialog} onOpenChange={(open) => { if (!open) handleDismissIneligible(); }}>
        <AlertDialog.Portal>
          <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/40 backdrop-blur-sm z-[100]" />
          <AlertDialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-lg p-6 max-w-md w-[90vw] z-[101]">
            {/* Warning icon header */}
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                <ExclamationCircleIcon className="w-6 h-6 text-amber-600" />
              </div>
              <AlertDialog.Title className="text-lg font-semibold text-gray-900">
                {ineligibilityReason === CONSENT_NOT_GIVEN
                  ? 'Consent is needed before a referral can be made'
                  : 'Unable to Accept Referral'}
              </AlertDialog.Title>
            </div>
            
            <AlertDialog.Description className="text-sm text-gray-600 mb-6">
              {getIneligibilityMessage(ineligibilityReason)}
            </AlertDialog.Description>
            
            {/* Helpful note about what they can do. A referral stopped for
                consent is not a postcode problem, so it must not suggest
                re-checking one. */}
            {ineligibilityReason !== CONSENT_NOT_GIVEN && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-6">
                <p className="text-sm text-gray-700">
                  If you believe this is an error, please double-check the postcode. You can also contact our support team for assistance.
                </p>
              </div>
            )}
            
            <div className="flex justify-end gap-3">
              {/* "Edit Address" is the fix for a postcode, and nonsense for a
                  missing consent: there the only honest way on is to go and
                  ask, so the dialog offers one way out. */}
              {ineligibilityReason !== CONSENT_NOT_GIVEN && (
                <AlertDialog.Cancel asChild>
                  <button
                    type="button"
                    onClick={handleDismissIneligible}
                    className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  >
                    Edit Address
                  </button>
                </AlertDialog.Cancel>
              )}
              <AlertDialog.Action asChild>
                <button
                  type="button"
                  onClick={handleIneligibleGoToDashboard}
                  className="px-4 py-2 text-sm font-semibold text-white bg-primary border border-transparent rounded-lg hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  Go to Dashboard
                </button>
              </AlertDialog.Action>
            </div>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>
    </>
  );
}


